import { Injectable } from '@angular/core'
import { HttpErrorResponse } from '@angular/common/http'
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects'
import {
    catchError,
    combineLatest,
    concatMap,
    EMPTY,
    forkJoin,
    from,
    map as rxMap,
    Observable,
    of,
    switchMap,
    take,
    takeUntil,
    tap,
    toArray,
    throwError,
} from 'rxjs'
import { PlotDataCacheService } from '../../shared/services/plot-data-cache.service'
import { plotDataActions } from './plot-data.actions'
import { Store } from '@ngrx/store'
import {
    hasDisplayPlotData,
    resetPlotData,
    setPlotDataForAxis,
    setPlotDataForBothAxes,
    setPlotDataForMultiMapping,
    setPlotDataColorForMultiMapping,
    filterPlotData,
    setPlotDataType,
    setMultipleInstanceData,
} from './plot-data'
import { resolveAxisNames, resolveMarkerAxesForLoadedMaps } from './plot-data-axis-utils'
import {
    isMappingProperty,
} from './mapping-property'
import { ToasterService } from '../../shared/services/toaster.service'
import { caseActions } from '../case/case.actions'
import { selectionActions } from '../selection/selection.actions'
import {
    selectFilterPoints,
    selectDisplayedSelectionsOnScatterPlot,
    selectFreeSelectionName,
    selectSelectionType,
} from '../selection/selection.selectors'
import { SelectionType } from '../selection/selection.state'
import { PlotAxis, DimensionDataWithDomain } from './plot-data.state'
import {
    selectLoadedSegObjectPropertyMaps,
    selectSharedPropertyNames,
    selectSharedPropertyNameSet,
    selectMarkerDimensionsByMapId,
} from '../case/case.selectors'
import { selectMarkerColor, selectMarkerX, selectMarkerY } from './plot-data.selectors'
import { MarkerDimension, SegObjectPropertyMap } from '../case/case.state'

type MapPlotEntry = {
    dataX: DimensionDataWithDomain
    dataY: DimensionDataWithDomain
    segSource: string
    mapName: string
    mapId: number
}

const PLOT_DATA_LOG = '[PlotData]'

@Injectable()
export class PlotDataEffects {
    constructor(
        private readonly actions$: Actions,
        private readonly plotDataCache: PlotDataCacheService,
        private readonly store: Store,
        private readonly toaster: ToasterService
    ) {}

    /** Reload plot when loaded maps or their properties change. */
    reloadPlotForLoadedMaps$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                caseActions.loadingSegObjectPropertiesDataSucceeded,
                caseActions.toggleLoadedSegObjectPropertyMap,
                plotDataActions.reloadPlotForLoadedMaps
            ),
            concatLatestFrom(() => [
                this.store.select(selectLoadedSegObjectPropertyMaps),
                this.store.select(selectSharedPropertyNames),
                this.store.select(selectSharedPropertyNameSet),
                this.store.select(selectMarkerDimensionsByMapId),
                this.store.select(selectMarkerX),
                this.store.select(selectMarkerY),
                this.store.select(selectSelectionType),
                this.store.select(selectFreeSelectionName),
            ]),
            switchMap(
                ([
                    action,
                    loadedMaps,
                    sharedNames,
                    sharedNameSet,
                    markerDimensionsByMapId,
                    markerX,
                    markerY,
                    selectionType,
                    freeSelectionName,
                ]) => {
                console.log(PLOT_DATA_LOG, 'reloadPlotForLoadedMaps triggered', {
                    trigger: action.type,
                    loadedMapCount: loadedMaps.length,
                    loadedMaps: loadedMaps.map(m => ({ id: m.id, name: m.name, segSource: m.segSource })),
                    sharedPropertyCount: sharedNames.length,
                    currentAxes: { x: markerX?.name, y: markerY?.name },
                })

                if (loadedMaps.length === 0) {
                    console.log(PLOT_DATA_LOG, 'reloadPlotForLoadedMaps → reset (no maps loaded)')
                    this.plotDataCache.clear()
                    resetPlotData()
                    const keepNamedFreeSelection =
                        selectionType === SelectionType.Free && !!freeSelectionName
                    // Clear loading before selection side-effects so a reducer error cannot leave the spinner up.
                    return from([
                        plotDataActions.clearMarkerAxes(),
                        plotDataActions.plotDrawn(),
                        ...(keepNamedFreeSelection
                            ? []
                            : [selectionActions.clearSelection({ allBuckets: true })]),
                    ])
                }

                const allMapsReady = loadedMaps.every(map => {
                    const markers =
                        markerDimensionsByMapId[Number(map.id)] ??
                        markerDimensionsByMapId[map.id]
                    return markers && markers.length >= 2
                })
                if (!allMapsReady) {
                    // Never wipe display data while waiting for newly selected maps' catalogs.
                    // Shared X/Y (or any already-drawn plot) must stay until multi-map data arrives.
                    console.log(PLOT_DATA_LOG, 'reloadPlotForLoadedMaps → waiting for all map properties', {
                        ready: loadedMaps.map(map => {
                            const markers =
                                markerDimensionsByMapId[Number(map.id)] ??
                                markerDimensionsByMapId[map.id]
                            return {
                                id: map.id,
                                name: map.name,
                                markerCount: markers?.length ?? 0,
                            }
                        }),
                        keepingDisplay: hasDisplayPlotData(),
                    })
                    return EMPTY
                }

                // Always use clustering-filtered shared names. Falling back to a single map's
                // raw property list can keep unloaded-map TSNE/UMAP axes and leave stale plot data.
                // Synthetic "mapping" is color-only and never stored in sharedPropertyNames.
                if (sharedNames.length < 2) {
                    console.log(
                        PLOT_DATA_LOG,
                        'reloadPlotForLoadedMaps → clear (fewer than 2 eligible axis properties)',
                        { sharedNames: sharedNames.slice(0, 5), loadedMapCount: loadedMaps.length }
                    )
                    if (loadedMaps.length > 1) {
                        this.toaster.error(
                            'Selected intensity maps have no shared properties for X/Y axes.',
                            'Cannot load plot'
                        )
                    }
                    resetPlotData()
                    return from([
                        plotDataActions.clearMarkerAxes(),
                        plotDataActions.plotDrawn(),
                    ])
                }

                // Keep axis *names* when they remain in the shared catalog. Do not require the
                // MarkerDimension's original map id — deselecting that map would otherwise reset
                // X/Y even though the same properties exist on a remaining map (re-resolved below).
                const { markerXName, markerYName } = resolveAxisNames(
                    markerX?.name && sharedNameSet.has(markerX.name) ? markerX.name : undefined,
                    markerY?.name && sharedNameSet.has(markerY.name) ? markerY.name : undefined,
                    sharedNames,
                    sharedNameSet
                )
                if (!markerXName || !markerYName) {
                    console.log(PLOT_DATA_LOG, 'reloadPlotForLoadedMaps → clear (could not resolve axis names)', {
                        markerXName,
                        markerYName,
                    })
                    resetPlotData()
                    return from([
                        plotDataActions.clearMarkerAxes(),
                        plotDataActions.plotDrawn(),
                    ])
                }

                const resolved = resolveMarkerAxesForLoadedMaps(
                    loadedMaps,
                    markerDimensionsByMapId,
                    markerXName,
                    markerYName
                )
                if (!resolved) {
                    console.log(PLOT_DATA_LOG, 'reloadPlotForLoadedMaps → clear (markers not found on loaded maps)', {
                        markerXName,
                        markerYName,
                    })
                    resetPlotData()
                    return from([
                        plotDataActions.clearMarkerAxes(),
                        plotDataActions.plotDrawn(),
                    ])
                }

                if (!resolved.markerX.source_bin || !resolved.markerY.source_bin) {
                    console.log(PLOT_DATA_LOG, 'reloadPlotForLoadedMaps → abort (missing source_bin URLs)')
                    this.toaster.error(
                        'Property data is missing download URLs.',
                        'Error loading plot'
                    )
                    resetPlotData()
                    return from([
                        plotDataActions.clearMarkerAxes(),
                        plotDataActions.plotDrawn(),
                    ])
                }

                if (loadedMaps.length === 1) {
                    console.log(PLOT_DATA_LOG, 'reloadPlotForLoadedMaps → single-map path', {
                        map: loadedMaps[0].name,
                        markerX: markerXName,
                        markerY: markerYName,
                        mapId: loadedMaps[0].id,
                    })
                    return of(
                        plotDataActions.loadDataBothAxes({
                            markerXAxis: resolved.markerX,
                            markerYAxis: resolved.markerY,
                        })
                    )
                }

                console.log(PLOT_DATA_LOG, 'reloadPlotForLoadedMaps → multi-map path', {
                    mapCount: loadedMaps.length,
                    markerX: markerXName,
                    markerY: markerYName,
                })
                return from([
                    plotDataActions.syncMarkerAxes({
                        markerX: resolved.markerX,
                        markerY: resolved.markerY,
                    }),
                    plotDataActions.loadMultiMappingPlotData({
                        markerXName,
                        markerYName,
                    }),
                ])
            })
        )
    })

    loadMultiMappingPlotData$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(plotDataActions.loadMultiMappingPlotData),
                concatLatestFrom(() => [
                    this.store.select(selectLoadedSegObjectPropertyMaps),
                    this.store.select(selectMarkerDimensionsByMapId),
                    this.store.select(selectMarkerColor),
                ]),
                switchMap(
                    ([
                        { markerXName, markerYName },
                        loadedMaps,
                        markerDimensionsByMapId,
                        markerColor,
                    ]) => {
                    this.plotDataCache.clearInFlight()
                    const colorByMapping = isMappingProperty(markerColor?.name)

                    console.log(PLOT_DATA_LOG, 'loadMultiMappingPlotData started', {
                        markerXName,
                        markerYName,
                        colorByMapping,
                        maps: loadedMaps.map(m => ({
                            id: m.id,
                            name: m.name,
                            segSource: m.segSource,
                        })),
                    })

                    const colorPropertyName =
                        markerColor && !colorByMapping ? markerColor.name : null

                    return from(loadedMaps).pipe(
                        takeUntil(
                            this.actions$.pipe(
                                ofType(
                                    caseActions.toggleLoadedSegObjectPropertyMap,
                                    caseActions.clearSegObjectPropertyMapSelection,
                                    plotDataActions.clearMarkerAxes
                                )
                            )
                        ),
                        concatMap((propertyMap, index) => {
                            const load = this.createMapLoad(
                                propertyMap,
                                markerDimensionsByMapId,
                                markerXName,
                                markerYName,
                                index
                            )
                            if (!load) return EMPTY
                            return load.pipe(
                                catchError(err => {
                                    console.error(PLOT_DATA_LOG, 'skipping map after load error', {
                                        mapName: propertyMap.name,
                                        mapId: propertyMap.id,
                                        err,
                                    })
                                    return EMPTY
                                })
                            )
                        }),
                        toArray(),
                        switchMap(entries => {
                            if (entries.length === 0) {
                                console.log(
                                    PLOT_DATA_LOG,
                                    'loadMultiMappingPlotData → abort (no map data loaded)'
                                )
                                this.store.dispatch(plotDataActions.plotDrawn())
                                return EMPTY
                            }
                            console.log(
                                PLOT_DATA_LOG,
                                'loadMultiMappingPlotData → all maps fetched, calling merge',
                                {
                                    entries: entries.map(e => ({
                                        mapName: e.mapName,
                                        mapId: e.mapId,
                                        segSource: e.segSource,
                                        xPoints: e.dataX.points.length,
                                        yPoints: e.dataY.points.length,
                                        domainX: e.dataX.domain,
                                        domainY: e.dataY.domain,
                                    })),
                                }
                            )
                            setPlotDataForMultiMapping(entries, { colorByMapping })
                            if (colorByMapping) return EMPTY
                            if (colorPropertyName) {
                                return this.loadColorPropertyForMaps(
                                    colorPropertyName,
                                    loadedMaps,
                                    markerDimensionsByMapId
                                )
                            }
                            this.reapplyScatterMembershipColors()
                            return EMPTY
                        }),
                        catchError(err => {
                            console.error(PLOT_DATA_LOG, 'loadMultiMappingPlotData failed', err)
                            this.toaster.httpError(err, 'Error loading plot data.')
                            this.store.dispatch(plotDataActions.plotDrawn())
                            return EMPTY
                        })
                    )
                })
            )
        },
        { dispatch: false }
    )

    private createMapLoad(
        propertyMap: SegObjectPropertyMap,
        markerDimensionsByMapId: Record<number, MarkerDimension[]>,
        markerXName: string,
        markerYName: string,
        mapIndex: number
    ): Observable<MapPlotEntry> | null {
        if (isMappingProperty(markerXName) || isMappingProperty(markerYName)) {
            console.log(PLOT_DATA_LOG, `load data for mapping ${mapIndex + 1} → skip (mapping is color-only)`, {
                mapName: propertyMap.name,
                markerXName,
                markerYName,
            })
            return null
        }

        const mapId = Number(propertyMap.id)
        const markers =
            markerDimensionsByMapId[mapId] ?? markerDimensionsByMapId[propertyMap.id]
        const rawMarkerX = markers?.find(m => m.name === markerXName)
        const rawMarkerY = markers?.find(m => m.name === markerYName)
        const markerX = rawMarkerX ? this.withMapContext(rawMarkerX, propertyMap) : undefined
        const markerY = rawMarkerY ? this.withMapContext(rawMarkerY, propertyMap) : undefined

        if (!markerX || !markerX.source_bin) {
            console.log(PLOT_DATA_LOG, `load data for mapping ${mapIndex + 1} → skip`, {
                mapName: propertyMap.name,
                mapId,
                markerXFound: !!markerX,
                markerXBin: markerX?.source_bin,
            })
            return null
        }
        if (!markerY || !markerY.source_bin) {
            console.log(PLOT_DATA_LOG, `load data for mapping ${mapIndex + 1} → skip`, {
                mapName: propertyMap.name,
                mapId,
                markerYFound: !!markerY,
                markerYBin: markerY?.source_bin,
            })
            return null
        }

        console.log(PLOT_DATA_LOG, `load data for mapping ${mapIndex + 1} started`, {
            mapName: propertyMap.name,
            mapId,
            segSource: propertyMap.segSource,
            markerX: markerXName,
            markerY: markerYName,
            sourceBinX: markerX.source_bin,
            sourceBinY: markerY.source_bin,
            domainX: { lower: markerX.lower_bound, upper: markerX.upper_bound },
            domainY: { lower: markerY.lower_bound, upper: markerY.upper_bound },
        })

        return forkJoin([
            this.plotDataCache.getDataForDimension(markerX, mapId),
            this.plotDataCache.getDataForDimension(markerY, mapId),
        ]).pipe(
            tap(([dataX, dataY]) => {
                console.log(PLOT_DATA_LOG, `load data for mapping ${mapIndex + 1} completed`, {
                    mapName: propertyMap.name,
                    mapId,
                    xPoints: dataX.points.length,
                    yPoints: dataY.points.length,
                    domainX: dataX.domain,
                    domainY: dataY.domain,
                })
            }),
            catchError(err => {
                console.error(PLOT_DATA_LOG, `load data for mapping ${mapIndex + 1} failed`, {
                    mapName: propertyMap.name,
                    mapId,
                    markerX: markerXName,
                    markerY: markerYName,
                    err,
                })
                return throwError(() => err)
            }),
            rxMap(([dataX, dataY]) => ({
                dataX,
                dataY,
                segSource: propertyMap.segSource ?? '',
                mapName: propertyMap.name ?? `map-${mapId}`,
                mapId,
            }))
        )
    }

    private withMapContext(
        marker: MarkerDimension,
        propertyMap: SegObjectPropertyMap
    ): MarkerDimension {
        return {
            ...marker,
            segobjectpropertymap: marker.segobjectpropertymap ?? propertyMap,
        }
    }

    loadDataForAxis$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(plotDataActions.loadDataForAxis),
                concatLatestFrom(() => [
                    this.store.select(selectLoadedSegObjectPropertyMaps),
                    this.store.select(selectMarkerDimensionsByMapId),
                    this.store.select(selectMarkerX),
                    this.store.select(selectMarkerY),
                ]),
                switchMap(
                    ([{ axis, marker }, loadedMaps, markerDimensionsByMapId, markerX, markerY]) => {
                    if (axis !== PlotAxis.COLOR && loadedMaps.length > 1) {
                        return EMPTY
                    }

                    // "mapping" is color-axis only — never use it for X/Y.
                    if (axis !== PlotAxis.COLOR && isMappingProperty(marker?.name)) {
                        this.toaster.error(
                            '"intensity map" can only be used for the color axis.',
                            'Unsupported'
                        )
                        this.store.dispatch(plotDataActions.plotDrawn())
                        return EMPTY
                    }

                    // Never HTTP-fetch the synthetic "mapping" property.
                    if (isMappingProperty(marker?.name)) {
                        if (
                            loadedMaps.length > 1 &&
                            markerX?.name &&
                            markerY?.name &&
                            !isMappingProperty(markerX.name) &&
                            !isMappingProperty(markerY.name)
                        ) {
                            this.store.dispatch(
                                plotDataActions.loadMultiMappingPlotData({
                                    markerXName: markerX.name,
                                    markerYName: markerY.name,
                                })
                            )
                            return EMPTY
                        }
                        this.toaster.error(
                            '"intensity map" requires multiple intensity maps and numeric X/Y axes.',
                            'Cannot color by intensity map'
                        )
                        this.store.dispatch(plotDataActions.plotDrawn())
                        return EMPTY
                    }

                    // Clearing color on multi-map: reload without mapping colors.
                    if (
                        axis === PlotAxis.COLOR &&
                        loadedMaps.length > 1 &&
                        marker === null &&
                        markerX?.name &&
                        markerY?.name
                    ) {
                        this.store.dispatch(
                            plotDataActions.loadMultiMappingPlotData({
                                markerXName: markerX.name,
                                markerYName: markerY.name,
                            })
                        )
                        return EMPTY
                    }

                    // Multi-map color-by-property: fetch the property per map and join by
                    // segSource + segment id (segment ids alone are not unique across maps).
                    if (axis === PlotAxis.COLOR && loadedMaps.length > 1 && marker) {
                        return this.loadColorPropertyForMaps(
                            marker.name,
                            loadedMaps,
                            markerDimensionsByMapId
                        )
                    }

                    if (marker === null) {
                        setPlotDataForAxis(null, axis)
                        this.reapplyScatterMembershipColors()
                        this.store.dispatch(plotDataActions.plotDrawn())
                        return EMPTY
                    }

                    return this.plotDataCache.getDataForDimension(marker).pipe(
                        tap(data => {
                            setPlotDataForAxis(data, axis)
                            // X/Y keep color channel; color-property load replaces it.
                            // Re-apply membership only when this wasn't a color-property load.
                            if (axis !== PlotAxis.COLOR) {
                                this.reapplyScatterMembershipColors()
                            }
                        }),
                        catchError(err => {
                            if (err instanceof HttpErrorResponse) {
                                this.toaster.httpError(err, 'Error loading plot data.')
                            } else {
                                this.toaster.error(
                                    err instanceof Error
                                        ? err.message
                                        : 'Plot data could not be combined for the selected axes.',
                                    'Error loading plot'
                                )
                            }
                            this.store.dispatch(plotDataActions.plotDrawn())
                            return EMPTY
                        })
                    )
                }
                )
            )
        },
        { dispatch: false }
    )

    /** Load a shared color property for each loaded intensity map and apply to plot points. */
    private loadColorPropertyForMaps(
        propertyName: string,
        loadedMaps: SegObjectPropertyMap[],
        markerDimensionsByMapId: Record<number, MarkerDimension[]>
    ): Observable<never> {
        return from(loadedMaps).pipe(
            concatMap(propertyMap => {
                const mapId = Number(propertyMap.id)
                const markers =
                    markerDimensionsByMapId[mapId] ?? markerDimensionsByMapId[propertyMap.id]
                const rawMarker = markers?.find(m => m.name === propertyName)
                if (!rawMarker?.source_bin) {
                    console.log(PLOT_DATA_LOG, 'color property missing on map → skip', {
                        mapName: propertyMap.name,
                        mapId,
                        propertyName,
                    })
                    return EMPTY
                }
                const marker = this.withMapContext(rawMarker, propertyMap)
                return this.plotDataCache.getDataForDimension(marker, mapId).pipe(
                    rxMap(data => ({
                        data,
                        segSource: propertyMap.segSource ?? '',
                    })),
                    catchError(err => {
                        console.error(PLOT_DATA_LOG, 'color property load failed for map', {
                            mapName: propertyMap.name,
                            mapId,
                            propertyName,
                            err,
                        })
                        return EMPTY
                    })
                )
            }),
            toArray(),
            tap(entries => {
                if (entries.length === 0) {
                    this.toaster.error(
                        `Could not load color property "${propertyName}" for the selected intensity maps.`,
                        'Error loading plot'
                    )
                    this.store.dispatch(plotDataActions.plotDrawn())
                    return
                }
                try {
                    setPlotDataColorForMultiMapping(entries)
                } catch (err) {
                    this.toaster.error(
                        err instanceof Error
                            ? err.message
                            : 'Plot data could not be combined for the color axis.',
                        'Error loading plot'
                    )
                    this.store.dispatch(plotDataActions.plotDrawn())
                }
            }),
            switchMap(() => EMPTY)
        )
    }

    loadDataForBothAxes$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(plotDataActions.loadDataBothAxes),
                // switchMap so intensity-map changes cancel in-flight loads; exhaustMap
                // could keep showing the previous map's clustering after unpicking it.
                switchMap(action => {
                    const mapId = action.markerXAxis.segobjectpropertymap?.id
                    const mapName = action.markerXAxis.segobjectpropertymap?.name ?? 'unknown'
                    const segSource = action.markerXAxis.segobjectpropertymap?.segSource ?? ''
                    console.log(PLOT_DATA_LOG, 'loadDataBothAxes started', {
                        mapName,
                        segSource,
                        markerX: action.markerXAxis.name,
                        markerY: action.markerYAxis.name,
                        domainX: {
                            lower: action.markerXAxis.lower_bound,
                            upper: action.markerXAxis.upper_bound,
                        },
                        domainY: {
                            lower: action.markerYAxis.lower_bound,
                            upper: action.markerYAxis.upper_bound,
                        },
                    })
                    return forkJoin([
                        this.plotDataCache.getDataForDimension(
                            action.markerXAxis,
                            action.markerXAxis.segobjectpropertymap?.id
                        ),
                        this.plotDataCache.getDataForDimension(
                            action.markerYAxis,
                            action.markerYAxis.segobjectpropertymap?.id
                        ),
                    ]).pipe(
                        // Abort HTTP when maps are toggled/cleared so deselect-all is not blocked
                        // on in-flight property downloads.
                        takeUntil(
                            this.actions$.pipe(
                                ofType(
                                    caseActions.toggleLoadedSegObjectPropertyMap,
                                    caseActions.clearSegObjectPropertyMapSelection,
                                    plotDataActions.clearMarkerAxes
                                )
                            )
                        ),
                        concatLatestFrom(() => this.store.select(selectLoadedSegObjectPropertyMaps)),
                        tap(([[dataX, dataY], loadedMaps]) => {
                            if (
                                mapId != null &&
                                !loadedMaps.some(m => m.id === mapId)
                            ) {
                                console.log(
                                    PLOT_DATA_LOG,
                                    'loadDataBothAxes → ignore stale result (map unloaded)',
                                    { mapId, mapName }
                                )
                                this.store.dispatch(plotDataActions.plotDrawn())
                                return
                            }
                            console.log(PLOT_DATA_LOG, 'loadDataBothAxes completed → setPlotDataForBothAxes', {
                                mapName,
                                xPoints: dataX.points.length,
                                yPoints: dataY.points.length,
                            })
                            setPlotDataForBothAxes(dataX, dataY, segSource)
                            this.reapplyScatterMembershipColors()
                        }),
                        catchError(err => {
                            console.error(PLOT_DATA_LOG, 'loadDataBothAxes failed', err)
                            if (err instanceof HttpErrorResponse) {
                                this.toaster.httpError(err, 'Error loading plot data.')
                            } else {
                                this.toaster.error(
                                    err instanceof Error
                                        ? err.message
                                        : 'Plot data could not be combined for the selected axes.',
                                    'Error loading plot'
                                )
                            }
                            this.store.dispatch(plotDataActions.plotDrawn())
                            return EMPTY
                        })
                    )
                })
            )
        },
        { dispatch: false }
    )

    filterPlotData$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(
                    selectionActions.setType,
                    selectionActions.saveNewHierarchicalSelectionRoot,
                    selectionActions.saveNewAutoCellTypeDetectionRoot,
                    selectionActions.savingHierarchicalSelectionRootSucceeded,
                    selectionActions.setHierarchicalSelectionRoot,
                    selectionActions.setHierarchicalSelectionNode,
                    selectionActions.saveNewHierarchicalSelectionNode,
                    selectionActions.deleteHierarchicalSelectionNode,
                    selectionActions.deleteHierarchicalSelectionNodeRecursively,
                    selectionActions.setAutoCellTypeDetectionRoot,
                    selectionActions.setAutoCellTypeDetectionNode,
                    selectionActions.saveNewAutoCellTypeDetectionNode,
                    selectionActions.deleteAutoCellTypeDetectionNode,
                    selectionActions.deleteAutoCellTypeDetectionNodeRecursively,
                    selectionActions.savingAutoCellTypeDetectionRootSucceeded
                ),
                concatLatestFrom(() => this.store.select(selectFilterPoints)),
                tap(([_, filterPoints]) =>
                    filterPlotData(
                        filterPoints.pointsThisLevel,
                        filterPoints.pointsLowerLevels
                    )
                )
            )
        },
        { dispatch: false }
    )

    setPlotDataType$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(selectionActions.setType),
                tap(selectionType => {
                    const plotType =
                        selectionType.selectionType == SelectionType.Free
                            ? 0
                            : selectionType.selectionType == SelectionType.Hierarchical
                            ? 1
                            : 2
                    setPlotDataType(plotType)
                })
            )
        },
        { dispatch: false }
    )

    resetPlotState$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(caseActions.selectCase),
                tap(() => resetPlotData())
            )
        },
        { dispatch: false }
    )

    loadMultipleInstanceData$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(
                    selectionActions.setVisibleSelectionsOnScatterPlot,
                    // Palette hex comes from selection.annotationColor; re-apply so the plot redraws.
                    selectionActions.setSelectionShaderColor
                ),
                concatLatestFrom(() => this.store.select(selectDisplayedSelectionsOnScatterPlot)),
                tap(([, selections]) => {
                    setMultipleInstanceData(selections)
                })
            )
        },
        { dispatch: false }
    )

    /**
     * Re-apply hierarchical/unsupervised membership colors after an axis reload.
     * Skips when a color-axis property is active (that owns point[3] / domainColor).
     */
    private reapplyScatterMembershipColors(): void {
        combineLatest([
            this.store.select(selectMarkerColor),
            this.store.select(selectDisplayedSelectionsOnScatterPlot),
        ])
            .pipe(take(1))
            .subscribe(([markerColor, selections]) => {
                if (markerColor || selections.length === 0) return
                setMultipleInstanceData(selections)
            })
    }
}
